import { useState } from 'react';
import { Link } from 'react-router-dom';

const NEWSLETTER_FORM_URL = 'https://readdy.ai/api/form/d9b36bihsavvukudokpg';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotValue = formData.get('website_alt')?.toString().trim();

    if (honeypotValue && honeypotValue !== '') {
      setStatus('success');
      setStatusMessage('Grazie per l\'iscrizione!');
      setEmail('');
      return;
    }

    formData.delete('website_alt');

    try {
      const response = await fetch(NEWSLETTER_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        setStatusMessage('Grazie per l\'iscrizione! Riceverai aggiornamenti sui nuovi prodotti.');
        setEmail('');
      } else if (serverMsg && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
        setStatus('error');
        setStatusMessage(serverMsg);
      } else {
        setStatus('error');
        setStatusMessage(serverMsg || 'Si è verificato un errore. Riprova più tardi.');
      }
    } catch {
      setStatus('error');
      setStatusMessage('Si è verificato un errore di rete. Riprova più tardi.');
    }
  };

  return (
    <footer className="bg-foreground-900 text-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-16">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="font-heading text-xl md:text-2xl font-bold tracking-widest block mb-6 whitespace-nowrap">
              ITAOBUY SPREAD
            </Link>
            <p className="text-sm text-foreground-300 leading-relaxed mb-6">
              La guida italiana più completa per scoprire, analizzare e utilizzare Itaobuy Spreadsheet. Guide aggiornate, categorie prodotti e risorse utili.
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-5">Navigazione</h4>
            <ul className="space-y-3">
              <li><Link to="/#categorie" className="text-sm text-foreground-300 hover:text-white transition-colors">Categorie</Link></li>
              <li><Link to="/#cos-e" className="text-sm text-foreground-300 hover:text-white transition-colors">Cos&apos;è</Link></li>
              <li><Link to="/articoli/itaobuy-spreadsheet-guida-completa" className="text-sm text-foreground-300 hover:text-white transition-colors">Guida SEO</Link></li>
              <li><Link to="/#faq" className="text-sm text-foreground-300 hover:text-white transition-colors">FAQ</Link></li>
              <li><Link to="/articoli/itaobuy-spreadsheet-sicuro" className="text-sm text-foreground-300 hover:text-white transition-colors">Sicurezza</Link></li>
            </ul>
          </div>

          {/* Social */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-5">Social</h4>
            <ul className="space-y-3">
              <li>
                <a href="#" className="flex items-center gap-2.5 text-sm text-foreground-300 hover:text-white transition-colors" target="_blank" rel="noopener nofollow">
                  <i className="ri-instagram-line text-base"></i> Instagram
                </a>
              </li>
              <li>
                <a href="#" className="flex items-center gap-2.5 text-sm text-foreground-300 hover:text-white transition-colors" target="_blank" rel="noopener nofollow">
                  <i className="ri-youtube-line text-base"></i> YouTube
                </a>
              </li>
              <li>
                <a href="#" className="flex items-center gap-2.5 text-sm text-foreground-300 hover:text-white transition-colors" target="_blank" rel="noopener nofollow">
                  <i className="ri-facebook-line text-base"></i> Facebook
                </a>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-2">Resta aggiornato</h4>
            <p className="text-xs text-foreground-300 mb-4">sulle novità</p>
            <form onSubmit={handleNewsletterSubmit} data-readdy-form="">
              {/* Honeypot */}
              <input
                type="text"
                name="website_alt"
                className="newsletter-website-alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
              <div className="flex items-center gap-2">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="La tua email"
                  required
                  className="flex-1 bg-white/10 border border-white/20 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-foreground-300 outline-none focus:border-white/40 transition-colors"
                />
                <button
                  type="submit"
                  className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-white text-foreground-900 rounded-full hover:bg-background-100 transition-all cursor-pointer"
                  aria-label="Iscriviti"
                >
                  <i className="ri-arrow-right-line text-sm"></i>
                </button>
              </div>
            </form>
            {status !== 'idle' && (
              <p className={`text-xs mt-3 ${status === 'success' ? 'text-accent-300' : 'text-red-400'}`}>
                {statusMessage}
              </p>
            )}
          </div>
        </div>

        {/* Decorative Brand */}
        <div className="mt-16 pt-10 border-t border-white/10 text-center">
          <span className="font-heading text-6xl md:text-8xl font-bold text-white/5 select-none tracking-widest">
            ITAOBUY
          </span>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-foreground-300">
          <p>&copy; 2026 Itaobuy Spread. Tutti i diritti riservati.</p>
          <div className="flex items-center gap-6">
            <a href="#" className="hover:text-white transition-colors whitespace-nowrap">Privacy Policy</a>
            <a href="#" className="hover:text-white transition-colors whitespace-nowrap">Termini di Utilizzo</a>
            <a href="#" className="hover:text-white transition-colors whitespace-nowrap">Affiliate Disclosure</a>
          </div>
        </div>
      </div>
    </footer>
  );
}